import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Drawer,
  Row,
  Form,
  notification,
} from 'antd';
import { ScheduleOfValues } from 'ontraccr-common';

import FormCurrencyInput from '../../common/inputs/FormCurrencyInput';
import OnTraccrTextInput from '../../common/inputs/OnTraccrTextInput';
import DisplayText from '../../common/text/DisplayText';
import OnTraccrButton from '../../common/buttons/OnTraccrButton';
import { currencyFormatter } from '../../helpers/inputParsers';

import {
  getNumberText,
} from './helpers';

function ScheduleOfValuesAddRow({
  templateId,
  newItemNumber,
  onClose,
  visible,
  handleSubmit,
  liveSummaryValues,
  isSubContract,
  isChangeOrder,
  isNonHoldback,
  isLoading,
  parentRecord,
}) {
  const [formRef, setFormRef] = useState();
  const [max, setMax] = useState(Number.MAX_VALUE);

  const parentItemNumber = parentRecord?.lineNumber;
  const subItemCount = parentRecord?.children?.length ?? 0;
  // The first sub-item takes over the parent's numbers, so nothing else changes.
  const isFirstSubItem = !!parentRecord && !subItemCount;

  useEffect(() => {
    if (formRef) {
      formRef.resetFields();
      formRef.setFieldsValue({
        itemNumber: newItemNumber.toString(),
      });
      if (isFirstSubItem) {
        formRef.setFieldsValue({ contractAmount: parentRecord.contractAmount });
      }
    }
  }, [formRef, newItemNumber, isChangeOrder, parentRecord, isFirstSubItem]);

  useEffect(() => {
    if (liveSummaryValues.length) {
      setMax(liveSummaryValues[0].totalContractAmount - liveSummaryValues[0].currentContractValue);
    }
  }, [liveSummaryValues]);

  // A first sub-item replaces its parent's amount rather than adding to the contract.
  const availableAmount = isFirstSubItem ? max + (parentRecord.contractAmount ?? 0) : max;

  const validateContractAmount = useCallback((value) => {
    if (value > availableAmount && !isChangeOrder && !isNonHoldback && !templateId) {
      notification.warn({
        key: 'addRowKey',
        message: 'Warning',
        description: 'Contract amount is greater than available contract amount',
      });
    } else {
      notification.close('addRowKey');
    }
  }, [templateId, availableAmount, isChangeOrder, isNonHoldback]);

  // The first sub-item inherits the parent's billing history, so it cannot be
  // worth less than what has already been progressed against the parent.
  const minContractAmount = isFirstSubItem ? Number(parentRecord.progressToDate ?? 0) : 0;

  const contractAmountRules = useMemo(() => {
    const rules = [{ required: true, message: 'Contract amount is required.' }];
    // Only the first sub-item inherits progress, so it is the only one with a floor.
    if (!parentRecord || minContractAmount <= 0) return rules;

    return rules.concat({
      type: 'number',
      min: minContractAmount,
      message: `Must be at least ${currencyFormatter(minContractAmount)}, the parent's progress to date.`,
    });
  }, [parentRecord, minContractAmount]);

  const title = useMemo(() => {
    if (parentRecord) {
      return ScheduleOfValues.getSubItemLabel({ parentItemNumber, index: subItemCount });
    }
    return getNumberText({ isChangeOrder, isSubContract, itemNumber: newItemNumber });
  }, [isSubContract, isChangeOrder, newItemNumber, parentRecord, parentItemNumber, subItemCount]);

  const onFinish = useCallback((values) => {
    if (!isFirstSubItem) return handleSubmit(values);
    // Carry the parent's unit details onto the row that takes over its totals.
    return handleSubmit({
      ...values,
      unitType: parentRecord.unitType,
      estimatedQuantity: parentRecord.estimatedQuantity,
      billingCode: parentRecord.billingCode,
    });
  }, [handleSubmit, isFirstSubItem, parentRecord]);

  return (
    <Drawer
      title={parentRecord ? `Add Sub-Item to Item ${parentItemNumber}` : 'Add Row'}
      visible={visible}
      onClose={onClose}
      width={750}
      maskClosable={false}
    >
      {isFirstSubItem && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 20 }}
          message={`Item ${parentItemNumber} will become view-only and total its sub-items.`}
        />
      )}
      <Form
        onFinish={onFinish}
        ref={setFormRef}
        className="schedule-of-values-form"
        style={{
          maxWidth: '95%',
        }}
      >
        <Form.Item
          name="itemNumber"
          label="Item Number"
        >
          <DisplayText title={title} />
        </Form.Item>
        <Form.Item
          name="description"
          label="Description"
          rules={[
            { required: true, message: 'Description is required' },
          ]}
        >
          <OnTraccrTextInput
            style={{ width: '100%' }}
            placeholder="Description"
          />
        </Form.Item>
        <FormCurrencyInput
          isNotDisplay
          name="contractAmount"
          label="Contract Amount"
          placeholder={0}
          rules={contractAmountRules}
          onChange={(value) => { validateContractAmount(value); }}
        />
        <Row justify="center">
          <OnTraccrButton
            htmlType="submit"
            title="Add"
            style={{
              marginTop: 10,
              width: 140,
              height: 42,
            }}
            roundStyle
            loading={isLoading}
            disabled={isLoading}
          />
        </Row>
      </Form>
    </Drawer>
  );
}

/* eslint-disable react/forbid-prop-types */
ScheduleOfValuesAddRow.propTypes = {
  templateId: PropTypes.string,
  newItemNumber: PropTypes.number.isRequired,
  onClose: PropTypes.func.isRequired,
  visible: PropTypes.bool,
  handleSubmit: PropTypes.func.isRequired,
  liveSummaryValues: PropTypes.array,
  isSubContract: PropTypes.bool,
  isChangeOrder: PropTypes.bool,
  isNonHoldback: PropTypes.bool,
  isLoading: PropTypes.bool,
  parentRecord: PropTypes.object,
};

ScheduleOfValuesAddRow.defaultProps = {
  templateId: null,
  visible: false,
  liveSummaryValues: [],
  isSubContract: false,
  isChangeOrder: false,
  isNonHoldback: false,
  isLoading: false,
  parentRecord: null,
};

export default ScheduleOfValuesAddRow;
